import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router-dom';
import { Map } from 'immutable';
import get from 'lodash/get';
import url from 'url';

import Brand from 'models/brand';

import { SIGN_IN_FORM_IMPRESSION, SIGN_IN_FORM_SUBMIT } from 'lib/analytics/events/index';

import signIn from 'action-creators/account/sign-in';
import resetPassword from 'action-creators/account/reset-password';
import addErrors from 'action-creators/account/add-errors';
import dismissError from 'action-creators/account/dismiss-error';
import trackEventCreator from 'action-creators/analytics/track-event';
import cleanupSignInState from 'action-creators/account/cleanup-sign-in-state';


import ConnectedSignIn from 'components/common/sign-in-form/connected-sign-in';
import InputEmail from 'components/common/input/email';
import InputPassword from 'components/common/input/password';
import TermsAndConditions from 'components/common/terms-and-conditions';

import BrandName from 'containers/common/brand-name';

const propTypes = {
  app: PropTypes.string.isRequired,
  brand: PropTypes.instanceOf(Brand).isRequired,
  enterpriseInvite: PropTypes.instanceOf(Map),
  trackEvent: PropTypes.func.isRequired,
};

const defaultProps = {
  enterpriseInvite: null,
};

const SIGN_IN_ERRORS = {
  resetPasswordEmail: 'Please provide email to reset password',
  signInEmail: 'Email is required',
  password: 'Password is required',
};

const DEFAULT_REDIRECT_URI = '/account/';

class SignInForm extends React.Component {
  constructor(props) {
    super(props);

    this.dismissError = this.dismissError.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.forgotPasswordForm = this.forgotPasswordForm.bind(this);
    this.toggleForgotPasswordForm = this.toggleForgotPasswordForm.bind(this);
    this.toggleForgotLoginForm = this.toggleForgotLoginForm.bind(this);
    this.onResetPassword = this.onResetPassword.bind(this);
    this.redirectURI = this.redirectURI.bind(this);

    this.state = {
      signInEmail: '',
      password: '',
      resetPasswordEmail: '',
      showForgotPasswordForm: false,
      showForgotLoginForm: false,
    };
  }

  componentDidMount() {
    this.props.trackEvent(SIGN_IN_FORM_IMPRESSION);
  }

  componentWillUnmount() {
    if (this.props.app === 'Sign In') {
      this.props.cleanupSignInState();
    }
  }

  dismissError(value) {
    this.props.dismissError({ error: value });
  }

  handleChange(value, e) {
    const newState = {};
    newState[value] = e.target.value;
    this.setState(newState);
  }

  onSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    // Validate everything is filled out
    const errors = {};

    // NOTE: Not all browsers fire the `change` event when autofilling email
    // and password fields. Consequently we can't rely on this.state to have
    // autofilled values for email and password so we need to use a hack and
    // look at the actual values in the input elements. See also:
    // * https://github.com/facebook/react/issues/1159
    // * http://stackoverflow.com/a/11710295/66675
    const currentState = {
      signInEmail: get(this, 'inputEmail.emailField.value', ''),
      password: get(this, 'inputPassword.password.value', ''),
    };

    ['signInEmail', 'password'].forEach((field) => {
      const value = currentState[field];
      if (value === '') { errors[field] = SIGN_IN_ERRORS[field]; }
    });
    if (!Object.keys(errors).length) {
      const signInResponse = this.props.signIn({
        email: currentState.signInEmail,
        password: currentState.password,
        redirectURI: this.redirectURI(),
        enterpriseInvite: this.props.enterpriseInvite,
      });

      this.props.trackEvent(SIGN_IN_FORM_SUBMIT);
    } else {
      this.props.setErrors(errors);
    }
  }

  onResetPassword() {
    // Validate everything is filled out
    const errors = {};

    if (!Object.keys(errors).length) {
      const signInResponse = this.props.resetPassword({
        email: this.state.resetPasswordEmail,
      });
    }
    this.setState({
      showForgotPasswordForm: false,
      showForgotLoginForm: false,
    });
  }

  redirectURI() {
    const queryParams = url.parse(this.props.routerLocation.search, true).query.redirect_uri;
    // check if undefined and return default if true, return queryParams in all other cases even empty string
    if (typeof queryParams !== 'undefined') {
      return queryParams;
    }

    return DEFAULT_REDIRECT_URI;
  }

  toggleForgotPasswordForm() {
    this.setState({ showForgotPasswordForm: !this.state.showForgotPasswordForm });
  }

  toggleForgotLoginForm() {
    this.setState({ showForgotLoginForm: !this.state.showForgotLoginForm });
  }

  forgotPasswordForm(instructions, buttonText) {
    return (
      <div className="text-size-xs-14 text-size-md-16 text-weight-light">
        {instructions}
        <div className="borderless-gray-input reset-password-input">
          <input
            type="email"
            key="reset-password-email"
            placeholder="email"
            onChange={(e) => { this.handleChange('resetPasswordEmail', e); }}
          />
        </div>
        <div className="row">
          <div className="col-md-1" />
          <div className="col-xs-12 col-md-10">
            <a>
              <div
                className={`btn-alternate text-center ${this.props.brand.isBestParking ? 'btn-bp' : null}`}
                onClick={this.onResetPassword}
              >
                {buttonText}
              </div>
            </a>
          </div>
          <div className="col-md-1" />
        </div>
      </div>
    );
  }

  render() {
    const errors = this.props.errors;

    return (
      <div>
        <div className="text-weight-bold text-size-xs-24 text-size-md-28">
          Sign In
        </div>
        <form onSubmit={this.onSubmit}>
          <InputEmail
            key="input-text-email"
            display="email"
            error={errors.get('signInEmail')}
            value="signInEmail"
            dismissError={this.dismissError}
            onChange={this.handleChange}
            ref={(input) => { this.inputEmail = input; }}
          />
          <InputPassword
            display="password"
            error={errors.get('password')}
            value="password"
            dismissError={this.dismissError}
            onChange={this.handleChange}
            ref={(input) => { this.inputPassword = input; }}
          />
        <TermsAndConditions />
          <div className="row join-parkwhiz">
            <div className="col-xs-12 col-md-10 col-md-offset-1">
              <button
                type="submit"
                className={`btn-block btn-primary text-center margin-bottom-40 sign-in-btn ${this.props.brand.isBestParking ? 'btn-bp' : null}`}
                onSubmit={this.onSubmit}
              >
                SIGN IN
              </button>
            </div>
          </div>
        </form>
        <div className="line-height-extra text-size-xs-14 text-size-md-16">
          <a onClick={this.toggleForgotPasswordForm}>
            FORGOT PASSWORD?
          </a>
          {this.state.showForgotPasswordForm ? this.forgotPasswordForm("Enter your email below. We'll send you a link to reset your password.", 'RESET PASSWORD') : null}
        </div>
        <ConnectedSignIn redirectURI={this.redirectURI()} />
        <div className="forgot-password text-size-xs-14 text-size-md-16">
          <div><a onClick={this.toggleForgotLoginForm}>
            HAVE A PASS BUT DON&apos;T KNOW YOUR LOGIN?
          </a></div>
          {this.state.showForgotLoginForm ? this.forgotPasswordForm("Enter your email below. We'll send you a link to set a password.", 'SEND ME A LINK') : null}
          <br />
        </div>
        <div className="margin-bottom-40 text-size-xs-12 text-size-md-14">NEW TO <BrandName className="uppercase" />? <Link to={{ pathname: '/account/signup/', search: `?redirect_uri=${encodeURIComponent(this.redirectURI())}` }}>REGISTER NOW!</Link></div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const {
    name: app,
  } = state.app;

  const {
    enterpriseInvite,
    errors,
  } = state.account;


  let { routerLocation } = ownProps;
  if (!routerLocation) {
    ({ location: routerLocation } = state.router);
  }

  return {
    app,
    enterpriseInvite,
    errors,
    routerLocation,
  };
};

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    signIn,
    dismissError,
    setErrors: addErrors,
    resetPassword,
    trackEvent: trackEventCreator,
    cleanupSignInState,
  }, dispatch)
);

SignInForm.propTypes = propTypes;
SignInForm.defaultProps = defaultProps;
export default connect(mapStateToProps, mapDispatchToProps)(SignInForm);
